import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Pencil, Plus, Search } from 'lucide-react';
import type { Company, Unit, UnitKind } from '@amai/contracts';
import { UNIT_KINDS, UNIT_KIND_LABELS } from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { blank, formatDocument, formatPhone } from '@/lib/format';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { VersionCard } from '@/components/VersionCard';
import { ReadField } from '@/components/ContextPanel';
import { ActiveBadge } from '@/components/StatusBadge';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { Button } from '@/components/Button';
import { Drawer, Modal } from '@/components/Modal';
import { Field, FormSection } from '@/components/FormSection';
import { MaskedInput } from '@/components/MaskedInput';
import { ErrorState, LoadingState } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { fillIfEmpty, useCepLookup, useCnpjLookup } from '@/lib/lookup';

export function EmpresaPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [editingCompany, setEditingCompany] = useState(false);
  const [creatingUnit, setCreatingUnit] = useState(false);
  const [editingUnit, setEditingUnit] = useState<Unit | null>(null);

  const company = useQuery({
    queryKey: ['company'],
    queryFn: () => api.get<Company>('/company'),
  });

  const units = useQuery({
    queryKey: ['units'],
    queryFn: () => api.get<{ data: Unit[] }>('/units'),
  });

  const columns: Array<Column<Unit>> = [
    { key: 'code', label: 'Código', mono: true, strong: true, render: (unit) => unit.code },
    { key: 'name', label: 'Unidade', strong: true, render: (unit) => unit.name },
    { key: 'kind', label: 'Tipo', render: (unit) => UNIT_KIND_LABELS[unit.kind] },
    {
      key: 'city',
      label: 'Cidade / UF',
      render: (unit) => (unit.city ? `${unit.city} / ${unit.uf ?? '—'}` : '—'),
    },
    { key: 'status', label: 'Situação', render: (unit) => <ActiveBadge active={unit.isActive} /> },
    {
      key: 'actions',
      label: '',
      width: 56,
      right: true,
      render: (unit) =>
        can('empresas:editar') ? (
          <Button
            small
            variant="ghost"
            icon={Pencil}
            aria-label={`Editar ${unit.name}`}
            onClick={() => setEditingUnit(unit)}
          />
        ) : null,
    },
  ];

  return (
    <>
      <WorkspaceHeader
        title="Empresa e unidades"
        actions={
          can('empresas:editar') && company.data ? (
            <Button icon={Pencil} onClick={() => setEditingCompany(true)}>
              Editar dados
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        {company.isPending && <LoadingState />}
        {company.isError && (
          <ErrorState message={errorMessage(company.error)} onRetry={() => void company.refetch()} />
        )}

        {company.data && (
          <div className="card" style={{ padding: 20, marginBottom: 20 }}>
            <div className="grid-2">
              <ReadField label="Razão social" value={company.data.name} />
              <ReadField label="Nome fantasia" value={blank(company.data.tradeName)} />
              <ReadField label="CNPJ" value={formatDocument(company.data.document)} mono />
              <ReadField
                label="Inscrição estadual"
                value={blank(company.data.stateRegistration)}
                mono
              />
              <ReadField label="E-mail" value={blank(company.data.email)} />
              <ReadField label="Telefone" value={formatPhone(company.data.phone)} mono />
              <ReadField
                label="Endereço"
                value={
                  company.data.street
                    ? `${company.data.street}, ${company.data.number ?? 's/n'}${
                        company.data.district ? ` — ${company.data.district}` : ''
                      }`
                    : '—'
                }
              />
              <ReadField
                label="Cidade / UF"
                value={company.data.city ? `${company.data.city} / ${company.data.uf ?? '—'}` : '—'}
              />
              <ReadField label="Fuso horário" value={company.data.timezone} mono />
            </div>
          </div>
        )}

        <div style={{ marginBottom: 22 }}>
          <VersionCard />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ fontSize: 15, margin: 0 }}>Unidades</h2>
          <div style={{ flex: 1 }} />
          {can('empresas:criar') && (
            <Button small icon={Plus} onClick={() => setCreatingUnit(true)}>
              Nova unidade
            </Button>
          )}
        </div>

        {units.isPending && <LoadingState />}
        {units.isError && (
          <ErrorState message={errorMessage(units.error)} onRetry={() => void units.refetch()} />
        )}
        {units.data && (
          <SmartDataTable
            caption="Unidades operacionais da empresa"
            columns={columns}
            rows={units.data.data}
          />
        )}
      </div>

      {company.data && (
        <CompanyFormDrawer
          open={editingCompany}
          company={company.data}
          onClose={() => setEditingCompany(false)}
          onSaved={() => {
            setEditingCompany(false);
            void queryClient.invalidateQueries({ queryKey: ['company'] });
          }}
        />
      )}

      <UnitFormModal
        open={creatingUnit || Boolean(editingUnit)}
        unit={editingUnit}
        onClose={() => {
          setCreatingUnit(false);
          setEditingUnit(null);
        }}
        onSaved={() => {
          setCreatingUnit(false);
          setEditingUnit(null);
          void queryClient.invalidateQueries({ queryKey: ['units'] });
        }}
      />
    </>
  );
}

function CompanyFormDrawer({
  open,
  company,
  onClose,
  onSaved,
}: {
  open: boolean;
  company: Company;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [tradeName, setTradeName] = useState('');
  const [document, setDocument] = useState('');
  const [stateRegistration, setStateRegistration] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [zip, setZip] = useState('');
  const [street, setStreet] = useState('');
  const [number, setNumber] = useState('');
  const [district, setDistrict] = useState('');
  const [city, setCity] = useState('');
  const [uf, setUf] = useState('');
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setName(company.name);
      setTradeName(company.tradeName ?? '');
      setDocument(company.document);
      setStateRegistration(company.stateRegistration ?? '');
      setEmail(company.email ?? '');
      setPhone(company.phone ?? '');
      setZip(company.zip ?? '');
      setStreet(company.street ?? '');
      setNumber(company.number ?? '');
      setDistrict(company.district ?? '');
      setCity(company.city ?? '');
      setUf(company.uf ?? '');
    }
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.patch<Company>('/company', body),
    onSuccess: onSaved,
  });

  const cnpj = useCnpjLookup();
  const cep = useCepLookup();

  /**
   * Puxa o cadastro da Receita e preenche o que está VAZIO — nunca por cima
   * do que o operador digitou.
   */
  const puxarCnpj = (digits: string) => {
    if (digits.length !== 14) return;
    cnpj.mutate(digits, {
      onSuccess: (data) => {
        setName((v) => fillIfEmpty(v, data.name));
        setTradeName((v) => fillIfEmpty(v, data.tradeName));
        setEmail((v) => fillIfEmpty(v, data.email));
        setPhone((v) => fillIfEmpty(v, data.phone));
        setZip((v) => fillIfEmpty(v, data.zip));
        setStreet((v) => fillIfEmpty(v, data.street));
        setNumber((v) => fillIfEmpty(v, data.number));
        setDistrict((v) => fillIfEmpty(v, data.district));
        setCity((v) => fillIfEmpty(v, data.city));
        setUf((v) => fillIfEmpty(v, data.uf));
      },
    });
  };

  const puxarCep = (digits: string) => {
    if (digits.length !== 8) return;
    cep.mutate(digits, {
      onSuccess: (data) => {
        setStreet((v) => fillIfEmpty(v, data.street));
        setDistrict((v) => fillIfEmpty(v, data.district));
        setCity((v) => fillIfEmpty(v, data.city));
        setUf((v) => fillIfEmpty(v, data.uf));
      },
    });
  };

  const errors = fieldErrors(mutation.error);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Editar dados da empresa"
      description="O que muda aqui vale para contratos, notas e documentos gerados"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={name.trim().length < 2}
            onClick={() =>
              mutation.mutate({
                name: name.trim(),
                tradeName: tradeName.trim() || null,
                document,
                stateRegistration: stateRegistration.trim() || null,
                email: email.trim() || null,
                phone: phone.trim() || null,
                zip: zip.trim() || null,
                street: street.trim() || null,
                number: number.trim() || null,
                district: district.trim() || null,
                city: city.trim() || null,
                uf: uf.trim().toUpperCase() || null,
              })
            }
          >
            Salvar
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <Field label="Razão social" htmlFor="co-name" error={errors.name}>
          <input id="co-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Nome fantasia" htmlFor="co-trade" error={errors.tradeName}>
          <input id="co-trade" className="input" value={tradeName} onChange={(e) => setTradeName(e.target.value)} />
        </Field>
        <div className="grid-2">
          <Field
            label="CNPJ"
            htmlFor="co-doc"
            error={errors.document}
            hint={
              cnpj.isPending
                ? 'Consultando a Receita…'
                : cnpj.isError
                  ? 'CNPJ não encontrado na Receita — preencha à mão'
                  : cnpj.isSuccess
                    ? `Dados da Receita aplicados · situação ${cnpj.data.status ?? '—'}`
                    : 'Ao completar, buscamos os dados na Receita'
            }
          >
            <div style={{ display: 'flex', gap: 8 }}>
              <MaskedInput
                id="co-doc"
                mask="document"
                value={document}
                onChange={(digits) => {
                  setDocument(digits);
                  puxarCnpj(digits);
                }}
              />
              <Button
                small
                icon={cnpj.isSuccess ? Check : Search}
                loading={cnpj.isPending}
                disabled={document.length !== 14}
                aria-label="Buscar dados do CNPJ na Receita"
                onClick={() => puxarCnpj(document)}
              />
            </div>
          </Field>
          <Field label="Inscrição estadual" htmlFor="co-ie" error={errors.stateRegistration}>
            <input id="co-ie" className="input input--mono" value={stateRegistration} onChange={(e) => setStateRegistration(e.target.value)} />
          </Field>
        </div>
        <div className="grid-2">
          <Field label="E-mail" htmlFor="co-email" error={errors.email}>
            <input id="co-email" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Field label="Telefone" htmlFor="co-phone" error={errors.phone}>
            <MaskedInput id="co-phone" mask="phone" value={phone} onChange={setPhone} />
          </Field>
        </div>
        <div className="grid-2">
          <Field label="CEP" htmlFor="co-zip" error={errors.zip}>
            <MaskedInput
              id="co-zip"
              mask="cep"
              value={zip}
              onChange={(digits) => {
                setZip(digits);
                puxarCep(digits);
              }}
            />
          </Field>
          <Field label="Bairro" htmlFor="co-district">
            <input id="co-district" className="input" value={district} onChange={(e) => setDistrict(e.target.value)} />
          </Field>
        </div>
        <div className="grid-2">
          <Field label="Endereço" htmlFor="co-street">
            <input id="co-street" className="input" value={street} onChange={(e) => setStreet(e.target.value)} />
          </Field>
          <Field label="Número" htmlFor="co-number">
            <input id="co-number" className="input input--mono" value={number} onChange={(e) => setNumber(e.target.value)} />
          </Field>
        </div>
        <div className="grid-2">
          <Field label="Cidade" htmlFor="co-city">
            <input id="co-city" className="input" value={city} onChange={(e) => setCity(e.target.value)} />
          </Field>
          <Field label="UF" htmlFor="co-uf" error={errors.uf}>
            <input id="co-uf" className="input input--mono" maxLength={2} value={uf} onChange={(e) => setUf(e.target.value.toUpperCase())} />
          </Field>
        </div>
      </FormSection>
    </Drawer>
  );
}

function UnitFormModal({
  open,
  unit,
  onClose,
  onSaved,
}: {
  open: boolean;
  unit: Unit | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<UnitKind>('PARQUE');
  const [city, setCity] = useState('');
  const [uf, setUf] = useState('AC');
  const [isActive, setIsActive] = useState(true);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  const formKey = open ? (unit?.id ?? 'new') : null;
  if (formKey !== loadedFor) {
    setLoadedFor(formKey);
    setCode(unit?.code ?? '');
    setName(unit?.name ?? '');
    setKind(unit?.kind ?? 'PARQUE');
    setCity(unit?.city ?? '');
    setUf(unit?.uf ?? 'AC');
    setIsActive(unit?.isActive ?? true);
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) => (unit ? api.patch(`/units/${unit.id}`, body) : api.post('/units', body)),
    onSuccess: onSaved,
  });

  const errors = fieldErrors(mutation.error);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={unit ? `Editar ${unit.code}` : 'Nova unidade'}
      description="Unidades separam estoque, custos e operação por local"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={code.trim().length < 1 || name.trim().length < 2}
            onClick={() =>
              mutation.mutate({
                code: code.trim().toUpperCase(),
                name: name.trim(),
                kind,
                city: city.trim() || null,
                uf: uf.trim().toUpperCase() || null,
                isActive,
              })
            }
          >
            {unit ? 'Salvar' : 'Criar unidade'}
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <div className="grid-2">
          <Field label="Código" htmlFor="un-code" error={errors.code} hint="Curto, ex.: PARQUE, ALMOX">
            <input id="un-code" className="input input--mono" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
          </Field>
          <Field label="Tipo" htmlFor="un-kind">
            <select id="un-kind" className="input" value={kind} onChange={(e) => setKind(e.target.value as UnitKind)}>
              {UNIT_KINDS.map((value) => (
                <option key={value} value={value}>
                  {UNIT_KIND_LABELS[value]}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Nome" htmlFor="un-name" error={errors.name}>
          <input id="un-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <div className="grid-2">
          <Field label="Cidade" htmlFor="un-city">
            <input id="un-city" className="input" value={city} onChange={(e) => setCity(e.target.value)} />
          </Field>
          <Field label="UF" htmlFor="un-uf" error={errors.uf}>
            <input id="un-uf" className="input input--mono" maxLength={2} value={uf} onChange={(e) => setUf(e.target.value.toUpperCase())} />
          </Field>
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          Unidade ativa
        </label>
      </FormSection>
    </Modal>
  );
}
